// disabling for entire file as window is being used many time in file, also  keeping important logs for better debugging
/* eslint-disable no-undef */
import { useEffect, useRef } from 'react';
import {
  NOTIFICATION_BUFFER_SECONDS,
  ORDERS_POLLING_INTERVAL,
} from '../common/constants';

const useOrderAlert = () => {
  const audioContextRef = useRef(null);
  const userInteractedRef = useRef(false);

  // Initialize AudioContext on mount
  useEffect(() => {
    if (!audioContextRef?.current) {
      try {
        audioContextRef.current = new (window?.AudioContext ||
          window?.webkitAudioContext)();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error creating AudioContext:', error);
      }
    }

    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {});
    }

    // Register Service Worker for mobile
    if ('serviceWorker' in navigator) {
      navigator?.serviceWorker?.register('/service-worker.js').catch(() => {});
    }

    return () => {
      if (
        audioContextRef?.current &&
        audioContextRef?.current?.state !== 'closed'
      ) {
        audioContextRef?.current?.close();
      }
    };
  }, []);

  // Track user interaction
  useEffect(() => {
    const handleInteraction = () => {
      userInteractedRef.current = true;
    };

    ['click', 'touchstart', 'keydown'].forEach((e) =>
      document.addEventListener(e, handleInteraction, { once: true }),
    );
  }, []);

  const playBubblePop = (audioContext) => {
    const playBubble = (startTime, frequency) => {
      const osc = audioContext?.createOscillator();
      const gain = audioContext?.createGain();
      osc?.connect(gain);
      gain?.connect(audioContext?.destination);
      osc.type = 'sine';
      osc?.frequency?.setValueAtTime(frequency, startTime);
      gain?.gain?.setValueAtTime(1, startTime);
      gain?.gain?.exponentialRampToValueAtTime(0.01, startTime + 0.15);
      osc?.start(startTime);
      osc?.stop(startTime + 0.15);
    };
    playBubble(audioContext?.currentTime, 600);
    playBubble(audioContext?.currentTime + 0.15, 800);
    playBubble(audioContext?.currentTime + 0.25, 1000);
    playBubble(audioContext?.currentTime + 0.35, 1200);
  };

  const playNotificationSound = (order) => {
    const orderNumber = order?.orderNumber;

    if (!audioContextRef?.current) {
      return;
    }

    try {
      const audioContext = audioContextRef?.current;
      playBubblePop(audioContext);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error playing sound:', error);
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent);
      const notificationBody = `Order #${orderNumber} requires immediate attention! Order currently is in ${order?.status} status`;
      if (isMobile && 'serviceWorker' in navigator) {
        navigator?.serviceWorker?.ready?.then((registration) => {
          registration?.active?.postMessage({
            type: 'SHOW_NOTIFICATION',
            title: '🚨 Critical Order Alert',
            options: {
              body: notificationBody,
              tag: `critical-order-${orderNumber}`,
              requireInteraction: true,
              silent: false,
            },
          });
        });
      } else {
        const notification = new Notification('🚨 Critical Order Alert', {
          body: notificationBody,
          tag: `critical-order-${orderNumber}`,
          requireInteraction: true,
          silent: false,
          data: {
            orderNumber,
            timestamp: new Date()?.toISOString(),
          },
        });

        notification?.addEventListener('click', () => {
          notification?.close();
          window?.focus();
        });

        setTimeout(() => {
          notification?.close();
        }, 8000);
      }
    }
  };

  const playNotificationSoundForNewOrder = (newOrders, hideOrderNumber) => {
    if (!audioContextRef?.current) {
      return;
    }

    // currently not performing anything if order page number is not 1, this logic may be changed in future
    if (hideOrderNumber) {
      return;
    }

    if (!newOrders || newOrders?.length === 0) {
      return;
    }

    const currentTime = Date.now();
    const pollingIntervalMs = ORDERS_POLLING_INTERVAL;
    const bufferMs = NOTIFICATION_BUFFER_SECONDS * 1000;
    const timeWindowMs = pollingIntervalMs + bufferMs;

    const recentOrders = newOrders.filter((order) => {
      if (!order?.createdAt) {
        return false;
      }

      const orderTime = new Date(order?.createdAt).getTime();
      const timeDiffMs = currentTime - orderTime;
      const timeDiffSeconds = Math.floor(timeDiffMs / 1000);
      const isRecent = timeDiffMs >= 0 && timeDiffMs <= timeWindowMs;
      // eslint-disable-next-line no-console
      console.log(
        `Order #${
          order?.orderNumber
        }: Created ${timeDiffSeconds}s ago, Window: ${
          timeWindowMs / 1000
        }s, Include: ${isRecent}`,
      );

      return isRecent;
    });

    // eslint-disable-next-line no-console
    console.log(
      `Filtered orders: ${recentOrders.length}/${newOrders.length} within ${
        timeWindowMs / 1000
      }s window`,
    );

    if (recentOrders.length === 0) {
      return;
    }

    try {
      const audioContext = audioContextRef?.current;
      playBubblePop(audioContext);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error playing sound:', error);
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator?.userAgent);

      const orderNumbers = recentOrders?.map(
        (orderItem) => `#${orderItem?.orderNumber}`,
      );

      const formattedOrderNumbers = orderNumbers?.join(', ');

      const orderText = recentOrders?.length === 1 ? 'Order' : 'Orders';
      const requiresText = recentOrders?.length === 1 ? 'requires' : 'require';

      const notificationTitle = '🚨 New Order Alert';
      const notificationBody = hideOrderNumber
        ? `New ${orderText} ${requiresText} immediate attention!`
        : `${orderText} ${formattedOrderNumbers} ${requiresText} immediate attention!`;
      const notificationTag = `new-orders-${Date.now()}`;

      if (isMobile && 'serviceWorker' in navigator) {
        navigator?.serviceWorker?.ready?.then((registration) => {
          registration?.active?.postMessage({
            type: 'SHOW_NOTIFICATION',
            title: notificationTitle,
            options: {
              body: notificationBody,
              tag: notificationTag,
              requireInteraction: true,
              silent: false,
            },
          });
        });
      } else {
        const notification = new Notification(notificationTitle, {
          body: notificationBody,
          tag: notificationTag,
          requireInteraction: true,
          silent: false,
          data: {
            orderNumbers: recentOrders?.map((order) => order?.orderNumber),
            timestamp: new Date()?.toISOString(),
            count: recentOrders?.length,
          },
        });

        notification?.addEventListener('click', () => {
          notification?.close();
          window?.focus();
        });

        setTimeout(() => {
          notification?.close();
        }, 8000);
      }
    }
  };

  return {
    playNotificationSound, // For critical order alerts (single order)
    playNotificationSoundForNewOrder, // For new order alerts (multiple orders)
  };
};

export default useOrderAlert;
